import { useEffect, useMemo, useRef, useState } from 'react';
import { SimpleShell } from '../shell/SimpleShell';
import { CouncilPicker } from '../CouncilPicker';
import { getUserEmail, getUserSub } from '../auth';
import { emitSetMyAssessMode, fetchAssessMode } from '../api/account';
import { CONFIG } from '../config';
import { usePlanningPermissions } from '../usePlanningPermissions';
import { useInfiniteScroll } from '../useInfiniteScroll';
import { errMessage, parseEelError, renderMarkdown, runEvent } from '../pageUtils';
import { LOST_CONNECTION, STILL_PROCESSING } from '../api/events';
import {
  listPlanningCases, getPlanningCase, emitCreatePlanningCase, emitUpdatePlanningCase, emitLinkDocument, emitLinkPolicyInput, emitCancelPlanningCase, emitDeletePlanningCase,
  MIN_PROPOSAL_LENGTH, MIN_REFINEMENT_LENGTH, type CaseType, type PlanningCase, type PlanningCaseDetail,
} from '../api/planningCases';
import { getPlanningAssessment, getAssessmentRunState, emitRequestPlanningAssessment, emitRetryPlanningAssessmentPdf, type PlanningAssessment } from '../api/planningAssessments';
import { DevReplyPanel } from '../components/DevReplyPanel';
import { EarlierChecks } from '../components/EarlierChecks';
import { DescriptionConversation, conversationFor } from '../components/DescriptionConversation';
import {
  listPlanningDocuments, getPlanningDocumentUploadUrl, getPlanningDocumentDownloadUrl,
  uploadPlanningDocumentToS3, emitCreatePlanningDocument,
} from '../api/planningDocuments';
import { listPolicyInputs, type PolicyInput } from '../api/policyInputs';
import { usePullToRefresh, PULL_THRESHOLD } from '../usePullToRefresh';
import { refreshWallet, TOKEN_DISCLAIMER, useTokenWallet } from '../api/tokenWallet';
import { COPYRIGHT_DISCLAIMER, PRE_APPLICATION_DISCLAIMER } from '../legal';
import LocateStep from './LocateStep';
import PreAppLocateStep from './PreAppLocateStep';
import { SiteMapPanel } from '../components/SiteMapView';
import { LOCATE_ENABLED, parseSavedLocation } from '../api/locate';
import { lookUpSiteAuthorities, matchAuthority } from '../api/lpa';
import { SiteLocationNotes } from '../components/SiteLocationNotes';
import { PdfThumb } from '../components/PdfThumb';
import { SwipeToDelete } from '../components/SwipeToDelete';
import { DEFAULT_SUPPORTING_CATEGORY, DOCUMENT_CATEGORIES, LOCATION_PLAN_CATEGORY, STATEMENT_CATEGORY } from '../documentCategories';

interface PickedFile { file: File; category: string; pages?: number | null }
const NATIONAL_JURISDICTION = 'nppf';
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const UNDO_MS = 5_000;

// Display only, so files with generic names can be told apart. The largest /Count value in the file is normally the
// page tree's total. Modern PDFs pack the page tree into compressed object streams, so those are inflated and scanned too.
async function inflate(bytes: Uint8Array): Promise<string> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new TextDecoder('latin1').decode(await new Response(stream).arrayBuffer());
}

async function estimatePageCount(file: File): Promise<number | null> {
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const text = new TextDecoder('latin1').decode(bytes);
    let max: number | null = null;
    const scan = (s: string) => {
      for (const m of s.matchAll(/\/Count\s+(\d+)/g)) {
        const n = Number(m[1]);
        if (Number.isFinite(n) && (max == null || n > max)) max = n;
      }
    };
    scan(text);
    for (const m of text.matchAll(/\/Type\s*\/ObjStm[^]*?stream\r?\n/g)) {
      const start = m.index! + m[0].length;
      let end = text.indexOf('endstream', start);
      if (end < 0) continue;
      if (text[end - 1] === '\n') end -= 1;
      if (text[end - 1] === '\r') end -= 1;
      try { scan(await inflate(bytes.subarray(start, end))); } catch { /* not a flate stream */ }
    }
    return max;
  } catch {
    return null;
  }
}

function scoreKind(score: number | undefined | null): 'pos' | 'warn' | 'neg' | 'neutral' {
  if (score == null) return 'neutral';
  if (score < 40) return 'pos';
  if (score < 64) return 'warn';
  return 'neg';
}

function fmtSize(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function fmtWhen(iso: string | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
const HOLD_LOADER_MS = 5_000;

async function pollUntil<T>(fn: () => Promise<T | undefined | null | false>, timeoutMs: number, intervalMs = 2_000): Promise<T | undefined> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const v = await fn();
    if (v) return v;
    if (Date.now() + intervalMs > deadline) return undefined;
    await sleep(intervalMs);
  }
}

// A big upload can outlast the 30 s event wait even though the work completes moments later; the caller verifies the outcome itself.
async function tolerateSlow(p: Promise<void>): Promise<void> {
  try { await p; } catch (e) {
    if (e instanceof Error && e.message.startsWith('Still processing')) return;
    throw e;
  }
}

const isNational = (p: PolicyInput) => !p.jurisdiction?.trim() || p.jurisdiction.trim().toLowerCase() === NATIONAL_JURISDICTION;

// One input per jurisdiction: the lender's own (Tenant) input replaces the shared (Global) one for the same jurisdiction.
function dedupeByJurisdiction(inputs: PolicyInput[]): PolicyInput[] {
  const byKey = new Map<string, PolicyInput>();
  for (const p of inputs) {
    const key = (p.jurisdiction ?? '').trim().toLowerCase();
    const cur = byKey.get(key);
    if (!cur || (cur.scope === 'Global' && p.scope === 'Tenant')) byKey.set(key, p);
  }
  return [...byKey.values()];
}

function Chip({ kind, children }: { kind: string; children: React.ReactNode }) {
  return <span className={`sx-chip${kind !== 'neutral' ? ` sx-chip-${kind}` : ''}`}>{children}</span>;
}

function caseStatusLabel(c: PlanningCase): string {
  if (c.assessmentStatus === 'Assessed') return 'Assessed';
  if (c.assessmentStatus === 'Assessing') return 'Assessing';
  if (c.assessmentStatus === 'Failed') return 'Failed';
  return 'Not assessed';
}

function caseChip(c: PlanningCase) {
  if (c.assessmentStatus === 'Assessed') return <Chip kind={scoreKind(c.latestScore)}>{c.latestRiskBand ?? 'Assessed'}{c.latestScore != null ? ` · ${c.latestScore}` : ''}</Chip>;
  if (c.assessmentStatus === 'Assessing') return <Chip kind="neutral">Assessing…</Chip>;
  if (c.assessmentStatus === 'Failed') return <Chip kind="neg">Failed</Chip>;
  return <Chip kind="neutral">Not assessed</Chip>;
}

const TOUCH_ONLY = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;


const PROPOSAL_PROMPTS = 'What you want to build or change, roughly how big (depth, height, storeys, number of homes), the materials, what is on the site now, and anything nearby that matters.';
const MAX_PROPOSAL_LENGTH = 8000;

export function AssessPage({ id }: { id?: string }) {
  return <SimpleShell>{id ? <AssessResult id={id} /> : <AssessForm />}</SimpleShell>;
}

// ─── Upload form ──────────────────────────────────────────────────────────────

interface Progress {
  caseId?: string; done: Map<File, string>; policiesLinked: boolean; located: boolean;
  // Applications whose authority comes from the located site: confirmed by the person, then saved on the case.
  areaConfirmed?: boolean; areaSaved?: boolean;
  // The authority the case is assessed under once confirmed, and the one the documents name (from locating them).
  jurisdiction?: string; namedAuthority?: string | null;
}

function useOutOfTokens(): boolean {
  const { balance } = useTokenWallet();
  return balance === 0;
}

function OutOfTokens() {
  return (
    <div className="sx-tokens-empty" role="status">
      <span>You have no tokens left. Each assessment uses one token — ask your administrator for more.</span>
    </div>
  );
}

// The person's choice once known in this session, so coming back to the form opens on it at once instead of loading it again.
let knownMode: { userId: string; mode: CaseType } | null = null;

function AssessForm() {
  const { can, loading: permLoading, error: permError } = usePlanningPermissions();
  const outOfTokens = useOutOfTokens();
  const [area, setArea] = useState('');
  // The person's last choice, kept on their profile so it follows them between devices; Application until it has loaded.
  const known = knownMode && knownMode.userId === getUserSub() ? knownMode.mode : null;
  const [caseType, setCaseType] = useState<CaseType>(known ?? 'Application');
  const [modeLoaded, setModeLoaded] = useState(known !== null);
  const modeTouched = useRef(false);
  // The loading icon waits for the saved choice and permissions, but never long: past this the form shows with its default,
  // and the saved choice still switches it if it arrives before the person has chosen.
  const [holdExpired, setHoldExpired] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setHoldExpired(true), HOLD_LOADER_MS);
    return () => window.clearTimeout(timer);
  }, []);
  const modeReady = modeLoaded || holdExpired;
  const preApp = caseType === 'PreApplication';
  // An application's local planning authority is found from where its site is, once located, rather than picked up front; only
  // where the site can't be located is it chosen first.
  const lpaFromSite = !preApp && LOCATE_ENABLED();
  useEffect(() => {
    if (modeLoaded) return;
    let cancelled = false;
    const userId = getUserSub();
    if (!userId) { setModeLoaded(true); return; }
    fetchAssessMode(userId)
      .then(m => {
        if (cancelled || modeTouched.current) return;
        knownMode = { userId, mode: m };
        setCaseType(m);
      })
      .catch(() => undefined) // Remembering the choice is a convenience: on failure the form opens on Application.
      .finally(() => { if (!cancelled) setModeLoaded(true); });
    return () => { cancelled = true; };
  }, []);
  function chooseMode(value: CaseType) {
    modeTouched.current = true;
    const userId = getUserSub();
    if (userId) knownMode = { userId, mode: value };
    setCaseType(value);
    setError(null);
    if (value !== caseType) void emitSetMyAssessMode(value).catch(() => undefined);
  }
  const [proposal, setProposal] = useState('');
  // A pre-application check starts with the map: the site is marked first, then its local planning authority is found from it.
  const [siteDone, setSiteDone] = useState(false);
  // A fresh map for each new check (after a cancel, or switching mode back).
  const [siteRound, setSiteRound] = useState(0);
  const [authority, setAuthority] = useState<{ loading: boolean; name: string | null; error: string | null; others?: string[] }>({ loading: false, name: null, error: null });
  // The site as last saved (JSON), so redrawing a pre-application check's boundary starts from it.
  const [savedSite, setSavedSite] = useState<string | null>(null);
  // An application whose authority, found from the located site, is not clear-cut and needs the person to confirm it.
  const [confirmingArea, setConfirmingArea] = useState(false);
  const [files, setFiles] = useState<PickedFile[]>([]);
  const previewUrls = useRef(new Map<File, string>());
  useEffect(() => { const urls = previewUrls.current; return () => { urls.forEach(u => URL.revokeObjectURL(u)); urls.clear(); }; }, []);
  const [stage, setStage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [locateCaseId, setLocateCaseId] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const progress = useRef<Progress>({ done: new Map(), policiesLinked: false, located: false });
  const cancelRequested = useRef(false);
  const [requestSent, setRequestSent] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [policy, setPolicy] = useState<PolicyInput[]>([]);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [recent, setRecent] = useState<PlanningCase[]>([]);
  // A deleted row disappears at once and is only really deleted once the undo window closes (there is no restore event).
  const [hiddenIds, setHiddenIds] = useState<string[]>([]);
  const [undoable, setUndoable] = useState<PlanningCase | null>(null);
  const undoTimer = useRef<number | undefined>(undefined);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState<string | null>(null);
  const [recentNext, setRecentNext] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState('');
  const [moreError, setMoreError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listPolicyInputs()
      // Signed in to Planning, only the shared library is used: an Elevations lender's own inputs are for its own app.
      .then(list => { if (!cancelled) setPolicy(list.filter(p => p.scope === 'Global' && p.outputStatus === 'Generated')); })
      .catch((e: Error) => { if (!cancelled) setPolicyError(e.message); })
      .finally(() => { if (!cancelled) setPolicyLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    listPlanningCases()
      .then(p => { if (!cancelled) { setRecent(p.items); setRecentNext(p.nextToken); } })
      .catch((e: Error) => { if (!cancelled) setRecentError(e.message); })
      .finally(() => { if (!cancelled) setRecentLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Changing what is being assessed means a retry must start a fresh case.
  const areas = useMemo(
    () => dedupeByJurisdiction(policy.filter(p => !isNational(p))).sort((a, b) => (a.jurisdiction ?? '').localeCompare(b.jurisdiction ?? '')),
    [policy],
  );
  const nationals = useMemo(() => dedupeByJurisdiction(policy.filter(isNational)), [policy]);
  const councilNames = useMemo(() => areas.map(a => a.jurisdiction ?? '').filter(Boolean), [areas]);
  const recentCouncils = useMemo(() => {
    const byLower = new Map(councilNames.map(n => [n.toLowerCase(), n]));
    const out: string[] = [];
    for (const c of recent) {
      const name = c.jurisdiction ? byLower.get(c.jurisdiction.trim().toLowerCase()) : undefined;
      if (name && !out.includes(name)) out.push(name);
      if (out.length === 5) break;
    }
    return out;
  }, [recent, councilNames]);
  const chosenArea = areas.find(a => a.jurisdiction?.toLowerCase() === area.trim().toLowerCase());
  const chosenPolicy = [...nationals, ...(chosenArea ? [chosenArea] : [])];

  // Changing what is being assessed means a retry must start a fresh case.
  const chosenAreaId = chosenArea?.id;
  useEffect(() => { progress.current = { done: new Map(), policiesLinked: false, located: false }; setSiteDone(false); setSiteRound(r => r + 1); }, [caseType]);
  // An authority chosen up front starts a fresh case if it changes; one found from the site (a pre-application check, or an
  // application once located) is just linked afresh.
  useEffect(() => {
    if (preApp || lpaFromSite) progress.current.policiesLinked = false;
    else progress.current = { done: new Map(), policiesLinked: false, located: false };
  }, [chosenAreaId]);

  // A case that never reached assessment (an abandoned upload) has nothing to show, so it stays out of the list.
  const started = useMemo(() => recent.filter(c => !c.deletedAt && !hiddenIds.includes(c.id) && (c.assessmentStatus === 'Assessed' || c.assessmentStatus === 'Assessing' || c.assessmentStatus === 'Failed')), [recent, hiddenIds]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return started;
    return started.filter(c => [c.name, c.jurisdiction, c.latestRiskBand, caseStatusLabel(c)].some(v => v?.toLowerCase().includes(q)));
  }, [started, query]);
  const sentinel = useInfiniteScroll(() => void loadMoreRecent(), !!recentNext && !loadingMore && !moreError, recent.length);

  const [dragOver, setDragOver] = useState(false);
  const busy = stage !== null;
  const hasStatement = files.some(f => f.category === STATEMENT_CATEGORY);
  const hasLocationPlan = files.some(f => f.category === LOCATION_PLAN_CATEGORY);
  const needsArea = areas.length > 0;
  const proposalLength = proposal.trim().length;
  const hasSubject = preApp ? siteDone && proposalLength >= MIN_PROPOSAL_LENGTH : files.length > 0 && hasStatement;
  // The pin's authority, when we have no local policy for it: no check can run there yet.
  // A site that crosses a boundary can only be one of the authorities it touches: offer just those we cover (and no recently
  // used list) when there are at least two to choose between.
  const siteOptions = useMemo(() => {
    if (!authority.name || !authority.others?.length) return null;
    const covered = [authority.name, ...authority.others].map(n => matchAuthority(n, councilNames)).filter((n): n is string => !!n);
    const unique = [...new Set(covered)];
    return unique.length >= 2 ? unique : null;
  }, [authority.name, authority.others, councilNames]);
  // A pre-application check's authority found from the pin with no doubt (one authority, and one we cover) needs no field.
  const lpaClear = preApp && !authority.loading && !authority.error && !!authority.name && !authority.others?.length
    && !!chosenArea && matchAuthority(authority.name, councilNames) === chosenArea.jurisdiction;
  const uncovered = (preApp || confirmingArea) && !!authority.name && !chosenArea && !matchAuthority(authority.name, councilNames) ? authority.name : null;
  const ready = hasSubject && (!needsArea || !!chosenArea || lpaFromSite) && (chosenPolicy.length > 0 || lpaFromSite) && !policyLoading && !outOfTokens;

  // A new tab lets the browser's own PDF viewer handle it, and closing the tab returns here (works on mobile too).
  function previewFile(file: File) {
    let url = previewUrls.current.get(file);
    if (!url) { url = URL.createObjectURL(file); previewUrls.current.set(file, url); }
    window.open(url, '_blank');
  }

  function removeFile(f: PickedFile) {
    const url = previewUrls.current.get(f.file);
    if (url) { URL.revokeObjectURL(url); previewUrls.current.delete(f.file); }
    setFiles(files.filter(x => x !== f));
  }

  function addFiles(list: ArrayLike<File> | null) {
    if (!list) return;
    const next = [...files];
    const problems: string[] = [];
    for (const f of Array.from(list)) {
      if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) { problems.push(`${f.name} is not a PDF`); continue; }
      if (f.size > MAX_FILE_BYTES) { problems.push(`${f.name} is too large (max 20 MB)`); continue; }
      if (next.some(x => x.file.name === f.name && x.file.size === f.size)) continue;
      const category = /location[\s_.-]*plan|red[\s_.-]*line/i.test(f.name) ? LOCATION_PLAN_CATEGORY
        : /planning[\s_.-]*statement/i.test(f.name) ? STATEMENT_CATEGORY
        : DEFAULT_SUPPORTING_CATEGORY;
      next.push({ file: f, category });
    }
    setFiles(next);
    for (const p of next) {
      if (p.pages !== undefined) continue;
      void estimatePageCount(p.file).then(pages => setFiles(cur => cur.map(x => (x.file === p.file ? { ...x, pages } : x))));
    }
    setError(problems.length ? problems.join('. ') + '.' : null);
  }

  useEffect(() => {
    if (busy) return;
    const onPaste = (e: ClipboardEvent) => {
      const pasted = e.clipboardData?.files;
      if (!pasted || pasted.length === 0) return;
      e.preventDefault();
      addFiles(pasted);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  });

  async function loadMoreRecent() {
    if (!recentNext || loadingMore) return;
    setLoadingMore(true); setMoreError(null);
    try {
      const p = await listPlanningCases({ nextToken: recentNext });
      setRecent(prev => [...prev, ...p.items.filter(c => !prev.some(x => x.id === c.id))]);
      setRecentNext(p.nextToken);
    } catch (e) {
      setMoreError(e instanceof Error ? e.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }

  async function ensureCase(performedBy: string | undefined): Promise<string> {
    const p = progress.current;
    if (p.caseId) return p.caseId;
    setStage('Creating your assessment…');
    const before = new Set((await listPlanningCases()).items.map(c => c.id));
    // A unique placeholder; the assessment replaces it with the site name it reads from the documents.
    const placeholder = `Untitled site ${crypto.randomUUID().slice(0, 8)}`;
    await runEvent(connId => emitCreatePlanningCase({
      name: placeholder, nameProvisional: true, tenantId: CONFIG.tenantId, jurisdiction: chosenArea?.jurisdiction, performedBy,
      ...(preApp ? { caseType: 'PreApplication' as const } : {}),
    }, connId));
    const created = await pollUntil(async () => (await listPlanningCases()).items.find(c => !before.has(c.id) && c.name === placeholder), 30_000);
    if (!created) throw new Error('Your assessment was created but could not be found. Please check Recent assessments below.');
    p.caseId = created.id;
    return created.id;
  }

  // A pre-application check starts on the map; its case is created once the pin is confirmed (see PreAppLocateStep).
  async function createPreAppCase(): Promise<string> {
    try {
      return await ensureCase(getUserEmail() ?? undefined);
    } finally {
      setStage(null);
    }
  }

  // The site is saved: find its local planning authority from the pin, and pick it if it is one we cover.
  async function siteMarked(caseId: string) {
    progress.current.located = true;
    setLocateCaseId(null);
    setSiteDone(true);
    await findAuthority(caseId);
  }

  /**
   * Looks up the local planning authority the case's saved site is in, and any other its outline reaches, and picks the site's
   * one when it is one we cover. Returns that match (null when not found or not covered) and the others.
   */
  async function findAuthority(caseId: string): Promise<{ match: string | null; others: string[] }> {
    setAuthority({ loading: true, name: null, error: null });
    // The authority always comes from this site: never one left over from an earlier check.
    setArea('');
    try {
      const saved = (await getPlanningCase(caseId))?.siteLocation ?? null;
      setSavedSite(saved);
      const site = parseSavedLocation(saved);
      const { name, others } = site ? await lookUpSiteAuthorities(site) : { name: null, others: [] };
      setAuthority({ loading: false, name, others, error: name ? null : "We couldn't tell which local planning authority your site is in." });
      const match = name ? matchAuthority(name, councilNames) : null;
      setArea(match ?? '');
      return { match, others };
    } catch (e) {
      setAuthority({ loading: false, name: null, error: errMessage(e, "We couldn't tell which local planning authority your site is in.") });
      return { match: null, others: [] };
    }
  }

  async function submit() {
    setError(null);
    cancelRequested.current = false;
    setRequestSent(false);
    const performedBy = getUserEmail() ?? undefined;
    const p = progress.current;
    try {
      const caseId = await ensureCase(performedBy);
      if (preApp) {
        // The description and authority are known only now, after the site was marked.
        setStage('Saving your description…');
        await runEvent(connId => emitUpdatePlanningCase(caseId, { description: proposal.trim(), jurisdiction: chosenArea?.jurisdiction, performedBy }, connId));
      }

      const pending = files.filter(f => !p.done.has(f.file));
      for (const [i, { file, category }] of pending.entries()) {
        setStage(`Uploading document ${i + 1} of ${pending.length}…`);
        const { url, fields, s3Key } = await getPlanningDocumentUploadUrl(file.name, 'application/pdf');
        await uploadPlanningDocumentToS3(url, fields, file);
        await tolerateSlow(runEvent(connId => emitCreatePlanningDocument({
          s3Key, filename: file.name, name: file.name, category, performedBy,
        }, connId), 60_000));
        // The malware scan finishes a few seconds after upload, so wait for a verdict rather than just for the row.
        setStage(`Checking document ${i + 1} of ${pending.length} for malware…`);
        const doc = await pollUntil(async () => {
          const d = (await listPlanningDocuments()).find(x => x.s3Key === s3Key);
          // A row already linked for an earlier file can't be this file's; keep waiting for its own.
          if (d && [...p.done.values()].includes(d.id)) return undefined;
          return d && d.scanStatus !== 'Pending' ? d : undefined;
        }, 120_000, 3_000);
        if (!doc) throw new Error(`"${file.name}" could not be saved or checked in time. Please try again.`);
        if (doc.scanStatus === 'Infected') throw new Error(`"${file.name}" contains malware and was deleted. Remove it and try again.`);
        if (doc.scanStatus === 'Failed') throw new Error(`"${file.name}" cannot be used. ${doc.scanDetail ?? 'It could not be checked for malware.'}`);
        // Link events have no workflow to report completion, so waiting on one only ever runs out the clock; the readiness poll below confirms the edges.
        await emitLinkDocument(caseId, doc.id, true);
        p.done.set(file, doc.id);
      }

      if (lpaFromSite && !p.areaConfirmed) {
        // The authority comes from where the site is, so the site is located before anything is linked to the case, then the
        // authority found there is offered for confirmation.
        setStage('Getting your documents ready…');
        let lastLink = Date.now();
        const attached = await pollUntil(async () => {
          const c = await getPlanningCase(caseId);
          if (!c) return undefined;
          if (Date.now() - lastLink > 8_000) {
            lastLink = Date.now();
            const linked = new Set(c.documents.map(d => d.id));
            for (const id of p.done.values()) if (!linked.has(id)) await emitLinkDocument(caseId, id, true);
          }
          return c.documents.length >= files.length ? c : undefined;
        }, 90_000, 2_000);
        if (!attached) throw new Error('Your documents were uploaded but are still being attached. Please try again in a moment.');
        if (!p.located && !attached.siteLocation) { setStage(null); setLocateCaseId(caseId); return; }
        setStage('Finding the local planning authority…');
        const found = await findAuthority(caseId);
        // Clear-cut (the site is in one authority we cover, and the documents agree or name none): carry straight on. Anything
        // else is put to the person.
        const named = p.namedAuthority ? matchAuthority(p.namedAuthority, councilNames) : null;
        if (!found.match || found.others.length > 0 || (p.namedAuthority && named !== found.match)) {
          setStage(null);
          setResuming(false);
          setConfirmingArea(true);
          return;
        }
        p.areaConfirmed = true;
        p.jurisdiction = found.match;
      }
      if (lpaFromSite && !p.areaSaved) {
        setStage('Saving the local planning authority…');
        await runEvent(connId => emitUpdatePlanningCase(caseId, { jurisdiction: p.jurisdiction, performedBy }, connId));
        p.areaSaved = true;
      }
      // Read from the progress record, not this render: the authority may have been settled earlier in this same call.
      const policies = lpaFromSite
        ? [...nationals, ...areas.filter(a => a.jurisdiction?.toLowerCase() === p.jurisdiction?.toLowerCase())]
        : chosenPolicy;

      if (!p.policiesLinked) {
        setStage('Preparing the planning policies…');
        for (const pi of policies) {
          await emitLinkPolicyInput(caseId, pi.id, true);
        }
        p.policiesLinked = true;
      }

      // Link-completion notifications can arrive before the new edges are readable; starting too early would assess an empty case.
      setStage('Getting everything ready…');
      let lastRelink = Date.now();
      const ready = await pollUntil(async () => {
        const c = await getPlanningCase(caseId);
        if (!c) return undefined;
        // A link event that never produced its edge would otherwise stall this wait for the full timeout; re-send it.
        if (Date.now() - lastRelink > 8_000) {
          lastRelink = Date.now();
          const linked = new Set(c.documents.map(d => d.id));
          for (const id of p.done.values()) if (!linked.has(id)) await emitLinkDocument(caseId, id, true);
        }
        return c.documents.length >= files.length && c.policyInputs.length >= policies.length ? c : undefined;
      }, 90_000, 2_000);
      if (!ready) throw new Error('Your documents were uploaded but are still being attached. Please try again in a moment.');

      // Confirm where the site is before the assessment uses it; an already-confirmed location is kept.
      if (LOCATE_ENABLED() && !p.located && !ready.siteLocation) {
        setStage(null);
        setLocateCaseId(caseId);
        return;
      }

      if (cancelRequested.current) return;
      setStage('Starting the assessment…');
      if (cancelRequested.current) return;
      // The request event is batch-notified, so no completion arrives until the whole assessment ends; wait for the case to leave NotAssessed instead.
      setRequestSent(true);
      await emitRequestPlanningAssessment(caseId, performedBy);
      await pollUntil(async () => {
        const c = await getPlanningCase(caseId);
        return c && c.assessmentStatus && c.assessmentStatus !== 'NotAssessed' ? c : undefined;
      }, 45_000, 1_500);
      void refreshWallet();
      window.location.hash = `#/assess/${caseId}`;
    } catch (e) {
      setError(errMessage(e, 'Something went wrong. Please try again.'));
      setStage(null);
      setResuming(false);
    }
  }

  // Deleting is for good once the undo window closes: the case, its documents, assessments and reports (Privacy Notice).
  async function commitDelete(c: PlanningCase) {
    try {
      await runEvent(connId => emitDeletePlanningCase(c.id, getUserEmail() ?? undefined, connId), 60_000);
      setRecent(prev => prev.filter(x => x.id !== c.id));
    } catch (e) {
      setDeleteError(`“${c.name}” could not be deleted. ${errMessage(e, 'Please try again.')}`);
    } finally {
      setHiddenIds(prev => prev.filter(id => id !== c.id));
    }
  }

  function queueDelete(c: PlanningCase) {
    // Starting a second delete settles the first straight away.
    if (undoable) { window.clearTimeout(undoTimer.current); void commitDelete(undoable); }
    setDeleteError(null);
    setHiddenIds(prev => [...prev, c.id]);
    setUndoable(c);
    undoTimer.current = window.setTimeout(() => { setUndoable(null); void commitDelete(c); }, UNDO_MS);
  }

  function undoDelete() {
    if (!undoable) return;
    window.clearTimeout(undoTimer.current);
    const id = undoable.id;
    setHiddenIds(prev => prev.filter(x => x !== id));
    setUndoable(null);
  }

  // Leaving the page inside the undo window still deletes what the user removed.
  const undoableRef = useRef(undoable);
  undoableRef.current = undoable;
  useEffect(() => () => {
    const c = undoableRef.current;
    if (!c) return;
    window.clearTimeout(undoTimer.current);
    void runEvent(connId => emitDeletePlanningCase(c.id, getUserEmail() ?? undefined, connId), 60_000).catch(() => undefined);
  }, []);

  /** Clears everything entered for a new check and returns to the start screen. */
  function resetToStart() {
    progress.current = { done: new Map(), policiesLinked: false, located: false };
    previewUrls.current.forEach(u => URL.revokeObjectURL(u));
    previewUrls.current.clear();
    setFiles([]);
    setProposal('');
    setStage(null);
    setResuming(false);
    setError(null);
    setLocateCaseId(null);
    setSiteDone(false);
    setSiteRound(r => r + 1);
    setAuthority({ loading: false, name: null, error: null });
    setConfirmingArea(false);
    setSavedSite(null);
    if (preApp || lpaFromSite) setArea('');
    window.location.hash = '#/assess';
    window.scrollTo(0, 0);
  }

  // Only possible before the assessment request has gone out; after that the token is spent and the analysis is running.
  // Before a case exists there is nothing to cancel on the server, so it just starts again.
  async function cancelStart() {
    const caseId = progress.current.caseId;
    if (requestSent || cancelling) return;
    if (!caseId) { resetToStart(); return; }
    cancelRequested.current = true;
    setCancelling(true);
    try {
      await runEvent(connId => emitCancelPlanningCase(caseId, getUserEmail() ?? undefined, connId), 30_000);
      resetToStart();
    } catch (e) {
      cancelRequested.current = false;
      const started = e instanceof Error && /already started/i.test(e.message);
      if (started) { window.location.hash = `#/assess/${caseId}`; return; }
      setError(errMessage(e, 'The assessment could not be cancelled.'));
      setStage(null);
      setResuming(false);
    } finally {
      setCancelling(false);
    }
  }

  // Pull down from the top of the form to fetch the latest assessments and token balance (no pull-to-refresh of its own in an installed app).
  const { pull, refreshing } = usePullToRefresh(async () => {
    const [p] = await Promise.all([listPlanningCases(), refreshWallet().catch(() => undefined)]);
    setRecent(p.items);
    setRecentNext(p.nextToken);
    setRecentError(null);
  }, !busy && !resuming && !locateCaseId);

  if (!permLoading && permError) {
    return <div className="sx-err">We couldn't check your permissions ({permError}). Please sign out and sign in again.</div>;
  }

  if (!permLoading && !can('planning-cases:write')) {
    return <div className="sx-err">You don't have permission to run assessments. Please contact your administrator.</div>;
  }

  // Keep the app's loading icon up until the person's last choice (application or pre-application) is known, so the
  // form never opens on one and then jumps to the other.
  if ((!modeLoaded || permLoading) && !holdExpired) {
    return (
      <div className="boot boot-cover" role="status" aria-label="Loading">
        <div className="boot-icon"><img src="./assets/loading-icon.png" alt="" /></div>
      </div>
    );
  }

  if (confirmingArea && !locateCaseId) {
    const namedAuthority = progress.current.namedAuthority;
    const namedMatch = namedAuthority ? matchAuthority(namedAuthority, councilNames) : null;
    const mismatch = !!chosenArea && !!namedAuthority && namedMatch !== chosenArea.jurisdiction;
    const crosses = authority.others ?? [];
    const caseId = progress.current.caseId;
    return (
      <div className="sx-form">
        <div>
          <h2 className="sx-h2">Confirm the local planning authority</h2>
          <p className="sx-note">The application is judged against this authority's local plan.</p>
        </div>
        {policyError ? (
          <div className="sx-err">Couldn't load the planning policies: {policyError}</div>
        ) : authority.loading ? (
          <div className="sx-field">
            <span className="sx-label">Local planning authority</span>
            <span className="sx-note">Finding the local planning authority for your site… <span className="skel" /></span>
          </div>
        ) : (
          <div className="sx-field">
            <label className="sx-label" htmlFor="sx-area-confirm">Local planning authority</label>
            {crosses.length > 0 && <p className="sx-warning" role="status">Your site crosses into <strong>{crosses.join(' and ')}</strong>. Choose the authority the application is made to.</p>}
            <CouncilPicker
              id="sx-area-confirm" options={siteOptions ?? councilNames} recent={siteOptions ? [] : recentCouncils} value={area} onChange={setArea}
              disabled={busy} placeholder="Start typing the local planning authority…"
            />
            {chosenArea && authority.name && matchAuthority(authority.name, councilNames) === chosenArea.jurisdiction && (
              <span className="sx-note">Found from where your site was located. Change it if that's wrong.</span>
            )}
            {authority.error && <span className="sx-note">{authority.error} Choose it from the list.</span>}
            {area.trim() && !chosenArea && <span className="sx-note">Choose a local planning authority from the list.</span>}
          </div>
        )}
        {mismatch && (
          <div className="sx-err" role="status">
            Your documents name {namedAuthority} as the local planning authority, but the site was located in {chosenArea!.jurisdiction}.
            Check that these are the right documents and that the site is in the right place, or choose the right authority above.
          </div>
        )}
        {uncovered && (
          <div className="sx-err" role="status">
            Your site is in {uncovered}. We don't cover {uncovered} yet, so an assessment can't run there. No token has been used.
            If that authority is wrong, choose the right one above.
          </div>
        )}
        {error && <div className="sx-err" role="alert">{error}</div>}
        {outOfTokens && <OutOfTokens />}
        <button
          type="button" className="sx-cta" disabled={!chosenArea || authority.loading || !!uncovered || policyLoading || outOfTokens}
          onClick={() => { progress.current.areaConfirmed = true; progress.current.jurisdiction = chosenArea?.jurisdiction; setConfirmingArea(false); setResuming(true); void submit(); }}
        >Assess</button>
        <button type="button" className="sx-cta sx-cta-2" disabled={!caseId} onClick={() => { setConfirmingArea(false); if (caseId) setLocateCaseId(caseId); }}>Change the site</button>
        <button type="button" className="sx-cta sx-cta-2" onClick={() => void cancelStart()} disabled={cancelling}>{cancelling ? 'Cancelling…' : 'Cancel'}</button>
      </div>
    );
  }

  if (resuming && !locateCaseId) {
    return (
      <div className="sx-progress" role="status" aria-live="polite">
        <div className="sx-spinner" />
        <h2>{stage ?? 'Starting the assessment…'}</h2>
        <p className="sx-note">Your location is saved. The analysis takes a minute or two.</p>
        {!requestSent && (
          <button type="button" className="sx-cta sx-cta-2" style={{ maxWidth: 260 }} onClick={() => void cancelStart()} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </button>
        )}
      </div>
    );
  }

  if (locateCaseId) {
    return (
      <LocateStep
        caseId={locateCaseId}
        preApplication={preApp}
        coveredAuthorities={councilNames}
        saved={preApp ? savedSite : undefined}
        onDone={preApp
          ? () => void siteMarked(locateCaseId)
          : info => { progress.current.located = true; progress.current.namedAuthority = info?.namedAuthority ?? null; setResuming(true); setLocateCaseId(null); void submit(); }}
        onCancel={() => void cancelStart()}
        cancelling={cancelling}
      />
    );
  }

  return (
    <>
      {(pull > 0 || refreshing) && (
        <div className="sx-pull" style={{ height: pull }} role="status" aria-live="polite">
          {refreshing
            ? <><span className="sx-spinner sx-pull-spin" />Refreshing…</>
            : pull >= PULL_THRESHOLD ? 'Release to refresh' : 'Pull to refresh'}
        </div>
      )}
      <div className="sx-mode" role="radiogroup" aria-label="What would you like to check?">
        {([['Application', 'I have an application'], ['PreApplication', "I'm thinking of applying"]] as const).map(([value, label]) => (
          <button
            key={value} type="button" role="radio" aria-checked={modeReady && caseType === value} disabled={busy || !modeReady}
            className={`sx-mode-opt${modeReady && caseType === value ? ' sx-mode-on' : ''}`}
            onClick={() => chooseMode(value)}
          >{label}</button>
        ))}
      </div>
      <div>
        <h1 className="sx-h1">{preApp ? 'Check a proposal before you apply' : 'Assess a planning application'}</h1>
        <p className="sx-lede">{preApp
          ? "Mark your site on a map, then describe what you'd like to do. We'll tell you how likely it is to get planning permission, and how you could improve your chances."
          : "Upload the planning documents and we'll provide a risk analysis of the application."}</p>
      </div>

      {preApp && !siteDone && modeReady && (
        <PreAppLocateStep
          key={`site-${siteRound}`} caseId={progress.current.caseId ?? null} createCase={createPreAppCase} coveredAuthorities={councilNames}
          onDone={id => void siteMarked(id)} onCancel={() => void cancelStart()} cancelling={cancelling} embedded
        />
      )}

      {(!preApp || siteDone) && (
      <form className="sx-form" onSubmit={e => { e.preventDefault(); if (ready && !busy && !uncovered) void submit(); }}>
        {preApp && (
          <div>
            <p className="sx-step-of">Step 3 of 3</p>
            <h2 className="sx-h2">About your proposal</h2>
          </div>
        )}
        {preApp && (
          <div className="sx-field">
            <span className="sx-label">Your site</span>
            <span className="sx-note">
              Marked on the map.{' '}
              <button type="button" className="sx-link" disabled={busy} onClick={() => progress.current.caseId && setLocateCaseId(progress.current.caseId)}>Redraw the boundary</button>
            </span>
          </div>
        )}
        {policyError ? (
          <div className="sx-err">Couldn't load the planning policies: {policyError}</div>
        ) : preApp && authority.loading ? (
          <div className="sx-field">
            <span className="sx-label">Local planning authority</span>
            <span className="sx-note">Finding the local planning authority for your site… <span className="skel" /></span>
          </div>
        ) : !lpaFromSite && !lpaClear && needsArea && (
          <div className="sx-field">
            <label className="sx-label" htmlFor="sx-area">Local planning authority</label>
            {preApp && !!authority.others?.length && <p className="sx-warning" role="status">Your site crosses into <strong>{authority.others.join(' and ')}</strong>. Choose the authority you would apply to.</p>}
            <CouncilPicker
              id="sx-area" options={siteOptions ?? councilNames} recent={siteOptions ? [] : recentCouncils} value={area} onChange={setArea}
              disabled={busy} placeholder="Start typing the local planning authority…"
            />
            {preApp && authority.error && <span className="sx-note">{authority.error} Choose it from the list.</span>}
            {area.trim() && !chosenArea && <span className="sx-note">Choose a local planning authority from the list.</span>}
          </div>
        )}
        {uncovered && (
          <div className="sx-err" role="status">
            Your site is in {uncovered}. We don't cover {uncovered} yet, so a check can't run there. Your site is saved, and no token
            has been used. If that authority is wrong, choose the right one above.
          </div>
        )}
        {!policyError && !policyLoading && policy.length === 0 && (
          <div className="sx-err">No planning policies are ready yet, so an assessment can't run. Please contact your administrator.</div>
        )}

        {preApp && (
          <div className="sx-field">
            <label className="sx-label" htmlFor="sx-proposal">Describe what you want to do</label>
            <span className="sx-note">{PROPOSAL_PROMPTS}</span>
            <textarea
              id="sx-proposal" className="sx-input sx-textarea" rows={8} value={proposal} maxLength={MAX_PROPOSAL_LENGTH} disabled={busy}
              placeholder="For example: a single-storey rear extension about 4 m deep across the back of our semi-detached house, with a flat roof and brick to match. The house is on a residential street and the garden backs onto fields."
              onChange={e => setProposal(e.target.value)}
            />
            <span className="sx-note" aria-live="polite">
              {proposalLength < MIN_PROPOSAL_LENGTH
                ? `A little more, please: at least ${MIN_PROPOSAL_LENGTH} characters.`
                : proposalLength < 200 ? 'More detail gives a firmer answer: size, height and materials help most.'
                : `${proposalLength.toLocaleString()} characters.`}
            </span>
          </div>
        )}

        <div className="sx-field">
          <span className="sx-label">{preApp ? 'Sketches or drawings (optional)' : 'Planning documents'}</span>
          <label
            htmlFor="sx-file-input"
            className={`sx-drop${dragOver ? ' sx-drop-over' : ''}`}
            onDragOver={e => { if (busy) return; e.preventDefault(); setDragOver(true); }}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false); }}
            onDrop={e => { e.preventDefault(); setDragOver(false); if (!busy) addFiles(e.dataTransfer.files); }}
          >
            <strong>{files.length ? 'Add more PDFs' : 'Choose PDFs to upload'}</strong>
            <span>
              {TOUCH_ONLY ? 'Tap to browse. ' : 'Drag and drop or paste PDFs here, or click to browse. '}
              {preApp
                ? 'Not needed, but a sketch, drawing or earlier advice as a PDF helps'
                : 'Include the planning statement, the red-line location plan and any supporting reports'}
            </span>
            <input id="sx-file-input" type="file" accept="application/pdf,.pdf" multiple disabled={busy} onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          </label>
          {files.length > 0 && (
            <ul className="sx-files">
              {files.map(f => (
                <li key={`${f.file.name}-${f.file.size}`} className="sx-file">
                  <PdfThumb file={f.file} label={`First page of ${f.file.name}`} />
                  <span className="sx-file-name">{f.file.name}</span>
                  <span className="sx-file-size">{fmtSize(f.file.size)}{f.pages ? ` · ${f.pages} ${f.pages === 1 ? 'page' : 'pages'}` : ''}</span>
                  <button type="button" className="sx-file-view" aria-label={`Preview ${f.file.name}`} onClick={() => previewFile(f.file)}>Preview</button>
                  <select
                    className="sx-file-cat" aria-label={`Category for ${f.file.name}`} value={f.category}
                    disabled={busy || progress.current.done.has(f.file)}
                    onChange={e => setFiles(files.map(x => (x === f ? { ...x, category: e.target.value } : x)))}
                  >
                    {DOCUMENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {!busy && <button type="button" className="sx-file-x" aria-label={`Remove ${f.file.name}`} onClick={() => removeFile(f)}>×</button>}
                </li>
              ))}
            </ul>
          )}
          {!preApp && files.length > 0 && hasStatement && !hasLocationPlan && (
            <p className="sx-warning" role="status">
              <strong>No Location Plan marked.</strong> Without one, the analysis rests on the planning statement alone: the site can only be placed approximately, and any planning designations found will be reported as being in the general area, not necessarily on the site. Whatever the planning statement says about the site's location, such as being in the Green Belt, is taken as fact. Mark a document as the Location Plan for a proper site check.
            </p>
          )}
          {!preApp && files.length > 0 && !hasStatement && (
            <p className="sx-note" role="status">Mark one document as the Planning Statement — the assessment can't run without it.</p>
          )}
        </div>

        {outOfTokens && <OutOfTokens />}
        {error && <div className="sx-err" role="alert">{error}</div>}

        <button className="sx-cta" type="submit" disabled={!ready || busy || !!uncovered}>{busy ? stage : preApp ? 'Check my proposal' : 'Assess'}</button>
        {busy && <p className="sx-note" style={{ margin: 0, textAlign: 'center' }}>{preApp ? 'Keep this page open.' : 'Keep this page open while your documents upload.'}</p>}
        {!busy && (preApp || files.length > 0 || !!progress.current.caseId) && (
          <button type="button" className="sx-cta sx-cta-2" disabled={cancelling} onClick={() => void cancelStart()}>{cancelling ? 'Cancelling…' : 'Cancel'}</button>
        )}
        {preApp && <p className="sx-note" style={{ margin: 0, textAlign: 'center', fontSize: 11.5 }}>{PRE_APPLICATION_DISCLAIMER}</p>}
        <p className="sx-note" style={{ margin: 0, textAlign: 'center', fontSize: 11.5 }}>{COPYRIGHT_DISCLAIMER}</p>
        <p className="sx-note" style={{ margin: 0, textAlign: 'center', fontSize: 11.5 }}>{TOKEN_DISCLAIMER}</p>
      </form>
      )}

      <section>
        <h2 className="sx-sec-h">Recent assessments</h2>
        {recentError ? (
          <div className="sx-err">Couldn't load recent assessments: {recentError}</div>
        ) : recentLoading ? (
          <div className="sx-stack"><span className="skel" style={{ width: '100%', height: 56 }} /><span className="skel" style={{ width: '100%', height: 56 }} /></div>
        ) : started.length === 0 && !recentNext ? (
          <p className="sx-note">Nothing yet — your assessments will appear here.</p>
        ) : (
          <>
          <input
            className="sx-input sx-recent-search" type="search" value={query} placeholder="Search assessments"
            aria-label="Search assessments" autoComplete="off"
            onChange={e => setQuery(e.target.value)}
          />
          {matches.length === 0 && query.trim() && <p className="sx-note">No assessments match “{query.trim()}”.</p>}
          <ul className="sx-recent">
            {matches.map(c => (
              <li key={c.id}>
                <SwipeToDelete onDelete={() => queueDelete(c)}>
                <a href={`#/assess/${c.id}`}>
                  <div className="sx-recent-main">
                    <div className="sx-recent-name">{c.name}</div>
                    <div className="sx-recent-meta">{[c.caseType === 'PreApplication' ? 'Pre-application check' : undefined, c.jurisdiction, fmtWhen(c.createdAt)].filter(Boolean).join(' · ')}</div>
                  </div>
                  {caseChip(c)}
                </a>
                <button type="button" className="sx-recent-del" aria-label={`Delete ${c.name}`} title="Delete"
                  onClick={() => queueDelete(c)}>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 10v6M14 10v6" />
                  </svg>
                </button>
                </SwipeToDelete>
              </li>
            ))}
          </ul>
          {moreError && (
            <div className="sx-err" role="alert">
              Couldn’t load older assessments: {moreError}{' '}
              <button type="button" className="sx-buy" onClick={() => void loadMoreRecent()}>Try again</button>
            </div>
          )}
          {recentNext && !moreError && <div ref={sentinel} className="sx-note" style={{ textAlign: 'center', minHeight: 24 }}>{loadingMore ? 'Loading…' : ''}</div>}
          </>
        )}
      </section>
      {deleteError && (
        <div className="sx-undo sx-undo-err" role="alert">
          <span>{deleteError}</span>
          <button type="button" onClick={() => setDeleteError(null)}>Dismiss</button>
        </div>
      )}
      {undoable && !deleteError && (
        <div className="sx-undo" role="status">
          <span>Deleted “{undoable.name}”</span>
          <button type="button" onClick={undoDelete}>Undo</button>
        </div>
      )}
    </>
  );
}

// ─── Result ───────────────────────────────────────────────────────────────────

const POLL_MS = 6_000;
// The branded PDF is attached by a follow-on workflow, so keep looking for it briefly after the score appears.
const PDF_WAIT_MS = 3 * 60_000;
const RESTART_WAIT_MS = 2 * 60_000;

function AssessResult({ id }: { id: string }) {
  const outOfTokens = useOutOfTokens();
  const [item, setItem] = useState<PlanningCaseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<PlanningAssessment | null>(null);
  const [assessmentError, setAssessmentError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfRetrying, setPdfRetrying] = useState(false);
  const [notesEl, setNotesEl] = useState<HTMLElement | null>(null);
  const [viewFooterEl, setViewFooterEl] = useState<HTMLElement | null>(null);
  const assessedSeenAt = useRef<number | null>(null);
  const ignoreFailedUntil = useRef(0);
  const restart = useRef<{ until: number; lastAssessedAt: string | undefined } | null>(null);
  const [revising, setRevising] = useState(false);
  const [revised, setRevised] = useState('');
  // Refine: say what to change, continuing the conversation of descriptions. New: describe the proposal afresh.
  const [reviseMode, setReviseMode] = useState<'refine' | 'new'>('refine');
  const [reviseError, setReviseError] = useState<string | null>(null);
  const preApp = item?.caseType === 'PreApplication';
  const anotherLabel = preApp ? 'Check another proposal' : 'Assess another application';

  useEffect(() => {
    let cancelled = false;
    getPlanningCase(id)
      .then(d => {
        if (cancelled) return;
        if (!d) throw new Error('Assessment not found');
        setItem(d); setError(null);
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [id, tick]);

  const latestId = item ? [...item.assessments].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]?.id ?? null : null;
  const rawStatus = item?.assessmentStatus;
  // A new run marks the case Assessing in a follow-on workflow, so straight after "Check again" the case can still read
  // Assessed with the previous result. Show it as running until the case catches up: it goes Assessing, records a new
  // result, fails, or two minutes pass.
  if (restart.current && (rawStatus === 'Assessing' || item?.lastAssessedAt !== restart.current.lastAssessedAt
    || (rawStatus === 'Failed' && Date.now() >= ignoreFailedUntil.current) || Date.now() > restart.current.until)) restart.current = null;
  const status = restart.current || ((rawStatus === 'Failed' || rawStatus === 'NotAssessed') && Date.now() < ignoreFailedUntil.current) ? 'Assessing' : rawStatus;
  // A problem starting a check stops mattering once the case moves on (a run is under way, or a new result has arrived).
  useEffect(() => { if (rawStatus === 'Assessing') setRetryError(null); }, [rawStatus]);
  useEffect(() => { setRetryError(null); }, [item?.lastAssessedAt]);
  const assessed = status === 'Assessed' && !!latestId;

  useEffect(() => {
    if (!assessed || !latestId) { setAssessment(null); return; }
    let cancelled = false;
    getPlanningAssessment(latestId)
      .then(a => { if (!cancelled) { setAssessment(a ?? null); setAssessmentError(null); } })
      .catch((e: Error) => { if (!cancelled) setAssessmentError(e.message); });
    return () => { cancelled = true; };
  }, [assessed, latestId, tick]);

  // Dev mode: a run in progress may be waiting for its prompt to be answered by hand instead of running.
  const [runState, setRunState] = useState<{ id: string; status: string; model: string | null } | null>(null);
  const [runStateError, setRunStateError] = useState<string | null>(null);
  const checkRun = status === 'Assessing' && !!latestId;
  useEffect(() => {
    if (!checkRun || !latestId) { setRunState(null); return; }
    let cancelled = false;
    getAssessmentRunState(latestId)
      .then(r => { if (!cancelled) { setRunState(r ? { id: latestId, ...r } : null); setRunStateError(null); } })
      .catch((e: Error) => { if (!cancelled) setRunStateError(e.message); });
    return () => { cancelled = true; };
  }, [checkRun, latestId, tick]);
  const awaitingReply = checkRun && runState?.id === latestId && runState.status === 'AwaitingReply';

  if (assessed && assessedSeenAt.current === null) assessedSeenAt.current = Date.now();
  const waitingForPdf = assessed && !assessment?.pdf && assessedSeenAt.current !== null && Date.now() - assessedSeenAt.current < PDF_WAIT_MS;
  const inFlight = !item ? !error : status === 'Assessing' || (status === 'Assessed' && (!latestId || waitingForPdf || !assessment));

  useEffect(() => {
    if (!inFlight || !item) return;
    const timer = setInterval(() => setTick(t => t + 1), POLL_MS);
    return () => clearInterval(timer);
  }, [inFlight, !!item]);

  async function retry() {
    setRetrying(true); setRetryError(null);
    // Show the run as started at once: confirmation that its workflow has run can take a while, and with nothing on screen
    // meanwhile the button looks dead and gets pressed again, starting (and charging for) a second run.
    ignoreFailedUntil.current = Date.now() + 20_000;
    restart.current = { until: Date.now() + RESTART_WAIT_MS, lastAssessedAt: item?.lastAssessedAt };
    assessedSeenAt.current = null;
    setTick(t => t + 1);
    try {
      await runEvent(connId => emitRequestPlanningAssessment(id, getUserEmail() ?? undefined, connId));
    } catch (e) {
      // A confirmation that only ran out of time, or whose connection dropped, says nothing about the run: it has usually
      // started, and the page is already watching the case for it, so that is not shown as an error. A real failure is.
      const message = e instanceof Error ? e.message : '';
      if (message !== STILL_PROCESSING && message !== LOST_CONNECTION) setRetryError(errMessage(e, 'Could not restart the check.'));
    } finally {
      void refreshWallet();
      setTick(t => t + 1);
      setRetrying(false);
    }
  }

  async function reviseAndRecheck() {
    if (!item) return;
    const description = revised.trim();
    const refines = reviseMode === 'refine';
    const min = refines ? MIN_REFINEMENT_LENGTH : MIN_PROPOSAL_LENGTH;
    if (description.length < min) {
      setReviseError(refines ? `Say what you'd change in at least ${min} characters.` : `Describe the proposal in at least ${min} characters.`);
      return;
    }
    setRetrying(true); setReviseError(null);
    try {
      // Always saved, even when the words match the last ones: whether they refine or replace the earlier descriptions changes too.
      await runEvent(connId => emitUpdatePlanningCase(item.id, { description, descriptionRefines: refines, performedBy: getUserEmail() ?? undefined }, connId, { description: item.description }));
      setRevising(false);
      await retry();
    } catch (e) {
      setReviseError(errMessage(e, 'Your changes could not be saved.'));
    } finally {
      setRetrying(false);
    }
  }

  async function retryPdf() {
    if (!assessment) return;
    setPdfRetrying(true); setPdfError(null);
    try {
      await emitRetryPlanningAssessmentPdf(assessment.id, getUserEmail() ?? undefined);
      assessedSeenAt.current = null;
      setTick(t => t + 1);
    } catch (e) {
      setPdfError(errMessage(e, 'Could not restart the report.'));
    } finally {
      setPdfRetrying(false);
    }
  }

  async function openPdf() {
    if (!assessment?.pdf) return;
    setPdfBusy(true); setPdfError(null);
    // Opened synchronously so mobile browsers don't treat the later navigation as an unwanted popup.
    const win = window.open('', '_blank');
    try {
      const url = await getPlanningDocumentDownloadUrl(assessment.pdf.id);
      if (win) win.location.href = url; else window.location.assign(url);
    } catch (e) {
      win?.close();
      setPdfError(errMessage(e, 'Could not open the report.'));
    } finally {
      setPdfBusy(false);
    }
  }

  if (error && !item) {
    return (
      <>
        <div className="sx-err">Couldn't load this assessment: {error}</div>
        <a className="sx-cta sx-cta-2" href="#/assess">Back</a>
      </>
    );
  }

  useEffect(() => { if (status === 'Assessed' || status === 'Failed') void refreshWallet(); }, [status]);

  const title = item?.name ?? <span className="skel" style={{ width: 220, height: 24 }} />;

  if (item?.deletedAt) {
    return (
      <>
        <h1 className="sx-h1">{title}</h1>
        <p className="sx-note">This assessment has been deleted.</p>
        <a className="sx-cta sx-cta-2" href="#/assess">Back to my assessments</a>
      </>
    );
  }

  if (status === 'Failed') {
    const detail = item?.assessmentError ? parseEelError(item.assessmentError) : null;
    return (
      <>
        <h1 className="sx-h1">{title}</h1>
        <div className="sx-err" role="alert">
          <strong>The assessment didn't complete.</strong>
          {detail && <div style={{ marginTop: 6, fontSize: 13 }}>{detail}</div>}
        </div>
        {retryError && <div className="sx-err">{retryError}</div>}
        {outOfTokens && <OutOfTokens />}
        <button className="sx-cta" onClick={() => void retry()} disabled={retrying || outOfTokens}>{retrying ? 'Restarting…' : 'Try again'}</button>
        <a className="sx-cta sx-cta-2" href="#/assess">{anotherLabel}</a>
      </>
    );
  }

  if (assessed && assessment) {
    const kind = scoreKind(assessment.score ?? item?.latestScore);
    const score = assessment.score ?? item?.latestScore;
    const band = assessment.riskBand ?? item?.latestRiskBand ?? 'Assessed';
    const checkedAgainst = (item?.policyInputs ?? []).map(p => p.jurisdiction || p.name);
    return (
      <>
        <div className={`sx-result sx-result-${kind}`}>
          {preApp && <div className="sx-result-kind">Pre-application check</div>}
          <div className="sx-result-site">{item?.name}{item?.jurisdiction ? ` · ${item.jurisdiction}` : ''}</div>
          <div className="sx-result-band">{band}</div>
          {score != null && (
            <div className="sx-gauge">
              <div className="sx-gauge-track" role="img" aria-label={`Risk score ${score} out of 100`}>
                <div className="sx-gauge-fill" style={{ width: `${Math.min(100, Math.max(2, score))}%` }} />
              </div>
              <div className="sx-gauge-lbl"><span>Risk score {score} / 100</span><span>Lower is better</span></div>
            </div>
          )}
        </div>

        <SiteMapPanel siteLocation={item?.siteLocation} caseId={item?.id} footer={notesEl} viewFooter={viewFooterEl} />

        {assessment.pdf ? (
          <button className="sx-cta" onClick={() => void openPdf()} disabled={pdfBusy}>{pdfBusy ? 'Opening…' : 'Download report (PDF)'}</button>
        ) : waitingForPdf ? (
          <button className="sx-cta" disabled>Preparing your report…</button>
        ) : (
          <>
            <div className="sx-err" role="alert">Report couldn't be prepared.</div>
            <button className="sx-cta" onClick={() => void retryPdf()} disabled={pdfRetrying}>{pdfRetrying ? 'Retrying…' : 'Retry'}</button>
          </>
        )}
        {pdfError && <div className="sx-err">{pdfError}</div>}
        {retryError && <div className="sx-err" role="alert">{retryError} Your previous result is shown below.</div>}

        {assessment.analysis && (
          <details className="sx-details">
            <summary>Read the full analysis</summary>
            <div className="sx-prose" dangerouslySetInnerHTML={{ __html: renderMarkdown(assessment.analysis) }} />
          </details>
        )}
        {assessmentError && <div className="sx-err">Couldn't refresh the analysis: {assessmentError}</div>}

        {preApp && item && (revising ? (
          <div className="sx-field">
            <div className="sx-mode" role="radiogroup" aria-label="How would you like to change it?">
              {([['refine', 'Refine this description'], ['new', 'Write a new description']] as const).map(([value, label]) => (
                <button
                  key={value} type="button" role="radio" aria-checked={reviseMode === value} disabled={retrying}
                  className={`sx-mode-opt${reviseMode === value ? ' sx-mode-on' : ''}`}
                  onClick={() => { if (value !== reviseMode) { setReviseMode(value); setRevised(''); setReviseError(null); } }}
                >{label}</button>
              ))}
            </div>
            <label className="sx-label" htmlFor="sx-revise">{reviseMode === 'refine' ? 'What would you change?' : 'Your new description'}</label>
            <span className="sx-note">{reviseMode === 'refine'
              ? "Say what you'd change, using the advice above. It's read together with your description so far, and the new report covers the proposal as it then stands."
              : 'Describe the whole proposal from scratch. Your earlier descriptions are not used.'} Checking again uses one token.</span>
            <textarea
              id="sx-revise" className="sx-input sx-textarea" rows={reviseMode === 'refine' ? 4 : 8} value={revised} maxLength={8000} disabled={retrying}
              placeholder={reviseMode === 'refine' ? 'For example: make the sign wooden and not illuminated, and fix it below the parapet.' : 'What you want to build, its size and height, materials, and where on the site it goes.'}
              onChange={e => setRevised(e.target.value)}
            />
            {reviseError && <div className="sx-err">{reviseError}</div>}
            {outOfTokens && <OutOfTokens />}
            <button className="sx-cta" onClick={() => void reviseAndRecheck()} disabled={retrying || outOfTokens || revised.trim().length < (reviseMode === 'refine' ? MIN_REFINEMENT_LENGTH : MIN_PROPOSAL_LENGTH)}>
              {retrying ? 'Starting…' : 'Check again'}
            </button>
            <button className="sx-cta sx-cta-2" onClick={() => setRevising(false)} disabled={retrying}>Cancel</button>
          </div>
        ) : (
          <>
            <details className="sx-details">
              <summary>Your description</summary>
              <DescriptionConversation messages={conversationFor(item.assessments, assessment.id)} fallback={assessment.proposalDescription ?? item.description} />
            </details>
            <button className="sx-cta sx-cta-2" onClick={() => { setReviseMode('refine'); setRevised(''); setReviseError(null); setRevising(true); }}>Revise and check again</button>
          </>
        ))}

        {preApp && item && (
          <EarlierChecks all={item.assessments} checks={item.assessments
            .filter(a => a.id !== latestId && a.status === 'Completed')
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt))} />
        )}

        <div ref={setViewFooterEl} style={{ display: 'flex', flexDirection: 'column', gap: 8 }} />
        <a className="sx-cta sx-cta-2" href="#/assess">{anotherLabel}</a>

        <p className="sx-note">
          {preApp
            ? `Based on your description${item?.documents.length ? ` and ${item.documents.length} document${item.documents.length === 1 ? '' : 's'}` : ''}`
            : `Based on ${item?.documents.length ?? 0} document${item?.documents.length === 1 ? '' : 's'}`}
          {checkedAgainst.length ? `, checked against ${checkedAgainst.join(', ')} policy` : ''}
          {assessment.assessedAt ? ` · ${fmtWhen(assessment.assessedAt)}` : ''}.
        </p>
        {preApp && <p className="sx-note">{PRE_APPLICATION_DISCLAIMER}</p>}
        <SiteLocationNotes siteLocation={item?.siteLocation} analysis />
        <div ref={setNotesEl} style={{ display: 'flex', flexDirection: 'column', gap: 8 }} />
      </>
    );
  }

  if (!item) {
    return (
      <>
        <h1 className="sx-h1">{title}</h1>
        <span className="skel" style={{ width: '100%', height: 120 }} />
      </>
    );
  }

  if (status === 'Cancelled') {
    return (
      <>
        <h1 className="sx-h1">{title}</h1>
        <p className="sx-note">This assessment was cancelled before it started, so no analysis was run and no token was used.</p>
        <a className="sx-cta sx-cta-2" href="#/assess">{anotherLabel}</a>
      </>
    );
  }

  if (status !== 'Assessing' && status !== 'Assessed') {
    // A pre-application check is assessed from its description, so it needs no documents.
    const hasDocs = preApp || item.documents.length > 0;
    const hasPolicies = item.policyInputs.length > 0;
    const missing = [!hasDocs && 'planning documents', !hasPolicies && 'planning policies'].filter(Boolean).join(' or ');
    return (
      <>
        <h1 className="sx-h1">{title}</h1>
        <p className="sx-note">
          This application was set up but its assessment was never started — usually because the upload was interrupted.
          {missing ? ` It has no ${missing} attached, so it can't be assessed; start a new assessment instead.` : ''}
        </p>
        {retryError && <div className="sx-err">{retryError}</div>}
        {!missing && outOfTokens && <OutOfTokens />}
        {!missing && <button className="sx-cta" onClick={() => void retry()} disabled={retrying || outOfTokens}>{retrying ? 'Starting…' : 'Start assessment'}</button>}
        <a className="sx-cta sx-cta-2" href="#/assess">{anotherLabel}</a>
      </>
    );
  }

  if (status === 'Assessed') {
    return (
      <>
        <h1 className="sx-h1">{title}</h1>
        {assessmentError ? (
          <div className="sx-err" role="alert">Couldn't load the assessment result: {assessmentError}. We'll keep trying.</div>
        ) : (
          <span className="skel" style={{ width: '100%', height: 120 }} />
        )}
        <a className="sx-cta sx-cta-2" href="#/assess">{anotherLabel}</a>
      </>
    );
  }

  if (awaitingReply && latestId) {
    return (
      <>
        {!item.nameProvisional && <h1 className="sx-h1">{title}</h1>}
        <DevReplyPanel key={latestId} caseId={item.id} assessmentId={latestId} model={runState?.model ?? null} onSubmitted={() => setTick(t => t + 1)} />
        <a className="sx-cta sx-cta-2" href="#/assess">{anotherLabel}</a>
      </>
    );
  }

  return (
    <>
      {!item.nameProvisional && <h1 className="sx-h1">{title}</h1>}
      {runStateError && <div className="sx-err">Couldn't check this run: {runStateError}. We'll keep trying.</div>}
      {retryError && <div className="sx-err">{retryError} We'll keep checking for it.</div>}
      <div className="sx-progress" role="status" aria-live="polite">
        <div className="sx-spinner" aria-hidden="true" />
        <h2>{preApp ? 'Checking your proposal…' : 'Assessing your application…'}</h2>
        <p className="sx-note" style={{ margin: 0 }}>
          {preApp ? "We're checking your description and site against the planning policies." : "We're reading your documents against the planning policies."} This usually takes a few minutes.
          You can leave this page — your result will be waiting in Recent assessments.
        </p>
      </div>
      {error && <div className="sx-err">Couldn't refresh: {error}. We'll keep trying.</div>}
      <a className="sx-cta sx-cta-2" href="#/assess">{anotherLabel}</a>
    </>
  );
}
